import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
var _dec, _class, _descriptor, _descriptor2, _descriptor3;
/*
 *                     NOTICE
 *            COPYRIGHT 2025 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { ValidationSeverity } from "abreact/form/ValidationIssue";
import * as AbContextComponentFactories from "abreact/incubator/context/AbContextComponentFactories";
import { AbReactContext } from "abreact/incubator/context/AbReactContext";
import { ManagedEditingState } from "abreact/incubator/ui/managed_editing/ManagedEditingState";
import { action, makeObservable, observable } from "mobx";
import * as React from "react";
export let EditManager = (_dec = observable.shallow, _class = class EditManager {
  constructor() {
    /**
     * Set to true to enable disabling save button if there are dynamic validation issues in any items
     * registered with this edit manager.
     */
    _defineProperty(this, "disableSaveIfThereAreDynamicValidationIssuesBlockingSave", false);
    _initializerDefineProperty(this, "items", _descriptor, this);
    // boolean is "was changed"
    _defineProperty(this, "_currentlyEditing", false);
    _defineProperty(this, "editingDueToParentPanel", false);
    // true when parent panel has made us editable as an editable item
    _initializerDefineProperty(this, "hasChangedItems", _descriptor2, this);
    _initializerDefineProperty(this, "_editingMode", _descriptor3, this);
    _defineProperty(this, "onChange", void 0);
    _defineProperty(this, "onEditing", void 0);
    _defineProperty(this, "ownerAllowsEditing", true);
    _defineProperty(this, "exclusiveEditCoordinator", void 0);
    _defineProperty(this, "editableItemModel", void 0);
    _defineProperty(this, "changeListeners", new Set());
    makeObservable(this);
    this.hasChangedItems = false;
    this._editingMode = ManagedEditingState.CANNOT_BE_EDITED;
  }
  get hasUnsavedChanges() {
    return this.hasChangedItems;
  }
  get currentlyEditing() {
    return this._currentlyEditing;
  }
  get editingMode() {
    // optionally check the validation registry for issues blocking save for registered items
    // and disable edit button if needed
    if (this.disableSaveIfThereAreDynamicValidationIssuesBlockingSave && this._editingMode === ManagedEditingState.EDITING_AND_CAN_BE_SAVED && this.allDynamicValidationIssues.some(issue => issue.severity === ValidationSeverity.FATAL_ERROR)) {
      return ManagedEditingState.EDITING_AND_CANNOT_BE_SAVED;
    }

    /*
      MhEditManager overrides hasUnsavedChanges to incorporate direct knowledge of datasources to be saved or unsaved value store changes.
      Those bits are both mobx observable.
      So here, we "fix" the editing state on the fly, in this mobx observable result.
      One gap: change listeners won't get called on the changes to hasUnsavedChanges, and might have stale editing mode.
      The only things that care about that change, we hope, are the save buttons, which are driven off of mobx observability.
      Little sketchy, but having challenges blending mobx and callbacks.
    */
    if (this._editingMode === ManagedEditingState.EDITING_AND_CANNOT_BE_SAVED && this.hasUnsavedChanges) {
      return ManagedEditingState.EDITING_AND_CAN_BE_SAVED;
    }
    return this._editingMode;
  }

  /** Attached edit manager to an exclusive edit coordinator.
   * This can only be called once, and will do nothing on the second and subsequent calls.
   * This should be called early in the lifecycle of the edit manager before much has happened.
   */
  setExclusiveEditCoordinator(coordinator) {
    if (this.exclusiveEditCoordinator) {
      // don't deal with reconnecting to new coordinator
      // we could, but its not a currently forseeable use case
      // guess this should log a warning
      return;
    }
    this.onEditCoordinatorChange = this.onEditCoordinatorChange.bind(this);
    coordinator.addChangeListener(this.onEditCoordinatorChange);
    this.exclusiveEditCoordinator = coordinator;

    // When edit() is called before setExclusiveEditCoordinator(), the ExclusiveEditCoordinator cannot yet be put into
    // edit mode. Here, we make sure that the ExclusiveEditCoordinator is put into edit mode once it is set.
    if (this.currentlyEditing) {
      this.exclusiveEditCoordinator.startEditingForItem(this);
    }
  }

  /**
   * For nested editing panels, the panel can be an editable item of the parent panel
   */
  setEditableItemModel(model) {
    this.editableItemModel = model;
    this.editableItemModel.setCallbacks({
      startEditing: () => this.onParentPanelStartEditing(),
      endEditing: () => this.onParentPanelEndEditing(),
      saveEditing: () => this.onParentPanelSaveEditing(),
      revertEditing: () => this.onParentPanelRevertEditing()
    });
  }
  unmount() {
    // unplug from edit coordinator
    if (this.exclusiveEditCoordinator) {
      this.exclusiveEditCoordinator.removeChangeListener(this.onEditCoordinatorChange);
      if (this.currentlyEditing) {
        this.exclusiveEditCoordinator.endEditingForItem(this);
      }
    }

    // unplug references for GC assistance (not sure if required, but won't hurt)
    this.items.clear();
    this.onChange = undefined;
    this.exclusiveEditCoordinator = undefined;
  }
  onEditCoordinatorChange() {
    this.updateVisibleState();
  }

  /**
   * Called by editable item to register itself with the edit manager.
   *
   * Formerly, items only registered when they could be edited. However, there is a use case
   * where an item needs to re-render when the container is editable even if the item itself
   * is not editable. For example, showing the lock icons with the reason why the item is not
   * editable.
   *
   * The default behavior is that the item is declared as editable when registered, but
   * optionally the second parameter can be used to have it registred but not editable.
   *
   * To change this after registration, the method setItemCanBeEdited can be called.
   */
  register(item, canBeEdited = true) {
    let entry = {
      wasChanged: false,
      canBeEdited: canBeEdited,
      dynamicValidationIssues: []
    };

    // dynamicValidationIssues must be mobx observable to fit in with ValidationProvider system for live validation updating
    makeObservable(entry, {
      dynamicValidationIssues: observable.ref // the whole array is replaced when issues change
    });
    this.items.set(item, entry);
    this.updateVisibleState();

    // Handle the case where an item is registered with the panel already in edit mode
    if (this.currentlyEditing && canBeEdited) {
      item.startEditing && item.startEditing();
    }
  }

  /** Called by editable item when dismounted, or no longer editable */
  deregister(item) {
    this.items.delete(item);
    this.updateVisibleState();
  }
  setItemWasChanged(item, wasChanged) {
    let itemEntry = this.items.get(item);
    if (itemEntry && itemEntry.wasChanged !== wasChanged) {
      itemEntry.wasChanged = wasChanged;
      this.updateVisibleState();
    }
  }
  setItemCanBeEdited(item, canBeEdited) {
    let itemEntry = this.items.get(item);
    if (itemEntry && itemEntry.canBeEdited !== canBeEdited) {
      itemEntry.canBeEdited = canBeEdited;
      this.updateVisibleState();

      // Handle the case where an item is registered with the panel already in edit mode
      if (this.currentlyEditing && canBeEdited) {
        item.startEditing && item.startEditing();
      }
    }
  }
  setDynamicValidationIssues(item, issues) {
    let itemEntry = this.items.get(item);
    if (itemEntry) {
      itemEntry.dynamicValidationIssues = issues; // will be mobx observable
    }
  }
  get allDynamicValidationIssues() {
    return Array.from(this.items.values()).flatMap(item => item.dynamicValidationIssues);
  }

  /** if ownerAllowsEditing is false, then editing is prevented.
   * Called by owner to explicitly disable editing.
   * If canEdit is true, editing is still subject to having editable items.
   * default: true
   * Undefined at present what will happen if in an editing state.
   * Generally this should be called when not in editing state.
   */
  setOwnerAllowsEditing(ownerAllowsEditing) {
    if (ownerAllowsEditing !== this.ownerAllowsEditing) {
      this.ownerAllowsEditing = ownerAllowsEditing;
      this.updateVisibleState();
    }
  }

  /** Computes current state from first principles, it's quick.
   * Also updates editableItemModel
   */
  updateVisibleState() {
    let oldChangedItems = this.hasChangedItems;
    if (this.currentlyEditing || this.editingDueToParentPanel) {
      this.hasChangedItems = Array.from(this.items.values()).some(itemEntry => itemEntry.wasChanged);
      if (this.currentlyEditing && this.exclusiveEditCoordinator) {
        // don't say this directly when editing via parent panel
        this.exclusiveEditCoordinator.setHasUnsavedChanges(this, this.hasChangedItems);
      }
    } else {
      this.hasChangedItems = false;
    }
    let oldEditingMode = this.editingMode;
    this._editingMode = this.computeMode();

    // notify the listener, e.g. the editable panel
    // optimize extraneous re-rendering by only calling when there is an actual change
    if (oldChangedItems !== this.hasChangedItems || oldEditingMode !== this.editingMode) {
      this.onChange && this.onChange();
    }

    // notify any other change listeners
    if (oldEditingMode !== this.editingMode) {
      this.callChangeListeners();
    }
    this.updateEditableItemModel();
  }
  updateEditableItemModel() {
    if (this.editableItemModel) {
      // Update the manager's own editable item model, which is used when nested
      // inside another edit manager.
      let canEdit = this.ownerAllowsEditing && this.hasEditableItems;
      this.editableItemModel.setEditable(canEdit);
      if (this.editingDueToParentPanel) {
        this.editableItemModel.setItemWasChanged(this.hasChangedItems);
      }
    }
  }

  /** Computes current mode */
  computeMode() {
    /* MH-23015 if we are already editing and all editable items disappear, we need to stay editing */
    if (!this.currentlyEditing) {
      if (!this.canBeEdited()) {
        return ManagedEditingState.CANNOT_BE_EDITED;
      }
      if (this.editingDueToParentPanel) {
        return ManagedEditingState.EDITING_DUE_TO_PARENT;
      }
      return ManagedEditingState.CAN_BE_EDITED;
    }
    if (!this.hasChangedItems) {
      return ManagedEditingState.EDITING_AND_CANNOT_BE_SAVED;
    }
    return ManagedEditingState.EDITING_AND_CAN_BE_SAVED;
  }
  canBeEdited() {
    // If the owner doesn't allow editing, then there's no editing.
    if (!this.ownerAllowsEditing) {
      return false;
    }

    // If exclusive edit coordinator doesn't allow editing, there's no editing
    if (this.exclusiveEditCoordinator && !this.exclusiveEditCoordinator.itemCanEdit(this)) {
      return false;
    }

    // If there are no items that can be edited, there's no editing\
    return this.hasEditableItems;
  }
  get hasEditableItems() {
    return Array.from(this.items.values()).some(item => item.canBeEdited);
  }
  edit() {
    // enter editing mode, call startEditing() on children
    if (this.exclusiveEditCoordinator) {
      this.exclusiveEditCoordinator.startEditingForItem(this);
    }
    this._currentlyEditing = true;
    this.transitionToEditing();
  }

  /** shared between local editing and editing due to parent */
  transitionToEditing() {
    this.resetItemEntries();
    this.startEditing();
    this.updateVisibleState();
    this.callManagerEditModeChangedOnAllItems(true);
    this.callAllItemsThatCanBeEdited(item => item.startEditing);
    this.onEditing && this.onEditing(true);
  }
  callManagerEditModeChangedOnAllItems(inEditMode) {
    Array.from(this.items.keys()).forEach(item => {
      item.managerEditModeChanged && item.managerEditModeChanged(inEditMode);
    });
  }

  // override to add additional behavior when editing starts
  startEditing() {}

  /** When editing starts, clear the 'wasChanged' states. Nothing can be changed when edit starts.
   * validity is less clear. Presumably items could have started invalid. But that really means not saveable, so what is that?
   * Not implementing validity yet, so that can be sorted later.
   */
  resetItemEntries() {
    this.items.forEach(value => {
      value.wasChanged = false;
    });
  }
  async save() {
    if (!this.canSave()) {
      return false;
    }
    let saved = await this.saveData();
    if (!saved) {
      return false;
    }
    this.endEditing();
    return true;
  }

  /**
   * Returns true if the manage is currently able to perform a save.
   *
   * For example, it might return false if there are known validation issues.
   */
  canSave() {
    return true;
  }

  /** For local save or parent panel save, shared code to save the data */
  async saveData() {
    // call save on all items and wait to see if successful
    let saved = await this.callAndReduceBooleanPromisesForItemsThatCanBeEdited(item => item.saveEditing);
    if (!saved) {
      return Promise.resolve(false);
    }

    // call hook to let derived class do additional processing
    let postSave = await this.postSaveProcessing();
    if (postSave === false) {
      return Promise.resolve(false);
    }

    // post save of null means there was nothing to save
    return Promise.resolve(true);
  }

  /** override for derived class specific processing, e.g. mhub datasource saving
   * @returns Promise resolving to true if successful save, false if unsuccessful, null if nothing to save
   */
  postSaveProcessing() {
    return Promise.resolve(null);
  }
  async cancel() {
    // call revert on children, wait on promises, call endEditing()
    await this.revertData();
    this.endEditing();
  }

  /** For local save or parent panel save, shared code to revert editing */
  revertData() {
    return this.callAndWaitOnPromisesForItemsThatCanBeEdited(item => item.revertEditing);
  }

  // override to handle other end of editing behavior in derived class
  endEditing() {
    this._currentlyEditing = false;
    this.transitionFromEditing();
    if (this.exclusiveEditCoordinator) {
      this.exclusiveEditCoordinator.endEditingForItem(this);
    }
  }

  /** Shared between local editing and editing due to parent. Handles leaving editing mode */
  transitionFromEditing() {
    this.callAllItemsThatCanBeEdited(item => item.endEditing);
    this.callManagerEditModeChangedOnAllItems(false);
    this.updateVisibleState();
    this.onEditing && this.onEditing(false);
  }
  performLocalValidation() {
    return Array.from(this.items.keys()).flatMap(item => item.validationIssues?.() ?? []);
  }
  async callAndReduceBooleanPromisesForItemsThatCanBeEdited(methodGetter) {
    // need to grab copy of item list for stability, prior to possibly async operation
    // Note: filter out any items that cannot be edited
    let items = Array.from(this.items.entries()).filter(itemAndItemEntry => itemAndItemEntry[1].canBeEdited).map(itemAndItemEntry => itemAndItemEntry[0]);
    let promises = items.map(item => {
      let method = methodGetter(item);
      let methodResult = method != null ? method.call(item) : true;
      if (typeof methodResult === "boolean") {
        return Promise.resolve(methodResult);
      } else {
        return methodResult;
      }
    });
    let results = await Promise.all(promises);
    return Promise.resolve(results.every(x => x));
  }
  async callAndWaitOnPromisesForItemsThatCanBeEdited(methodGetter) {
    // need to grab copy of item list for stability, prior to possibly async operation
    // Note: filter out any items that cannot be edited
    let items = Array.from(this.items.entries()).filter(itemAndItemEntry => itemAndItemEntry[1].canBeEdited).map(itemAndItemEntry => itemAndItemEntry[0]);
    let promises = items.map(item => {
      let method = methodGetter(item);
      let methodResult = method != null ? method.call(item) : undefined;
      if (typeof methodResult !== "object") {
        return Promise.resolve();
      } else {
        return methodResult;
      }
    });
    await Promise.all(promises);
    return Promise.resolve();
  }
  callAllItemsThatCanBeEdited(methodGetter) {
    let itemEntries = Array.from(this.items.entries());
    for (let [item, itemEntry] of itemEntries) {
      let method = methodGetter(item);
      method && itemEntry.canBeEdited && method.call(item);
    }
  }
  callChangeListeners() {
    for (let func of Array.from(this.changeListeners)) {
      func();
    }
  }
  addChangeListener(onChange) {
    this.changeListeners.add(onChange);
  }
  removeChangeListener(onChange) {
    this.changeListeners.delete(onChange);
  }
  onParentPanelStartEditing() {
    // enter editing mode, call startEditing() on children
    this.editingDueToParentPanel = true;
    this.transitionToEditing();
  }
  onParentPanelEndEditing() {
    this.editingDueToParentPanel = false;
    this.transitionFromEditing();
  }
  onParentPanelSaveEditing() {
    return this.saveData();
  }
  onParentPanelRevertEditing() {
    return this.revertData();
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "items", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return new Map();
  }
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "hasChangedItems", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "_editingMode", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "unmount", [action], Object.getOwnPropertyDescriptor(_class.prototype, "unmount"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "register", [action], Object.getOwnPropertyDescriptor(_class.prototype, "register"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "deregister", [action], Object.getOwnPropertyDescriptor(_class.prototype, "deregister"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setDynamicValidationIssues", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setDynamicValidationIssues"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "updateVisibleState", [action], Object.getOwnPropertyDescriptor(_class.prototype, "updateVisibleState"), _class.prototype), _class);
export let EditManagerProvider = AbContextComponentFactories.abContextProviderFactory("EditManagerProvider");
let editManagerHocFunc = AbContextComponentFactories.abContextHocFactory("withEditManager", ({
  editManager
}) => {
  return {
    editManager
  };
});
export function withEditManager(base) {
  return editManagerHocFunc(base);
}

/**
 * React hook that returns the edit manager in context, if any.
 */
export function useEditManager() {
  const context = React.useContext(AbReactContext);
  return context?.editManager;
}
//# sourceMappingURL=EditManager.js.map
